import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "../../components/ui-components/Toast";
import { fmtMoney, fmtDate, evaluateException, exceptionTone } from "./invoiceFormat";
import { listInvoices, getInvoice, confirmInvoice, rejectInvoice, getAttachments, fetchAttachmentBlob } from "./invoiceReviewApi";

const REJECT_REASONS = ["Duplicate", "Wrong amount", "Not our invoice", "Not an invoice", "Unreadable", "Fraud suspected", "Other"];
const eurosFromCents = (c) => (c == null || Number.isNaN(Number(c)) ? "" : Number(c) / 100);
const centsFromEuros = (e) => Math.round(Number(e) * 100);
const UNCERTAIN = 0.9;

const InvoiceReviewDetailPage = () => {
  const { id } = useParams();
  const invId = Number(id);
  const [params] = useSearchParams();
  const statusList = params.get("status") || "pending,needs_review";
  const navigate = useNavigate();
  const qc = useQueryClient();

  const { data: queue = [] } = useQuery(["invoices", statusList], () => listInvoices(statusList));
  const { data: detail, isLoading } = useQuery(["invoice", invId], () => getInvoice(invId), { enabled: !!invId });
  const { data: attachments = [] } = useQuery(["invoice-att", invId], () => getAttachments(invId), { enabled: !!invId });

  const [form, setForm] = useState(null);
  const [attIdx, setAttIdx] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const [previewErr, setPreviewErr] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [rejReason, setRejReason] = useState(REJECT_REASONS[0]);
  const [rejNote, setRejNote] = useState("");

  useEffect(() => {
    if (detail) setForm({
      vendor: detail.vendor || "", invoice_number: detail.invoice_number || "", invoice_date: detail.invoice_date || "",
      period_start: detail.period_start || "", period_end: detail.period_end || "", currency: detail.currency || "",
      amount_cents: detail.amount_cents ?? "",
    });
  }, [detail]);

  useEffect(() => {
    let revoked = false, url = null;
    setPreviewErr(false); setBlobUrl(null);
    if (attachments.length > 0) {
      fetchAttachmentBlob(invId, attachments[attIdx]?.idx ?? 0)
        .then((blob) => { if (!revoked) { url = URL.createObjectURL(blob); setBlobUrl(url); } })
        .catch(() => { if (!revoked) setPreviewErr(true); });
    }
    return () => { revoked = true; if (url) URL.revokeObjectURL(url); };
  }, [invId, attIdx, attachments]);

  const idxInQueue = useMemo(() => queue.findIndex((q) => q.id === invId), [queue, invId]);
  const goTo = (i) => { if (i >= 0 && i < queue.length) navigate(`/invoice-review/${queue[i].id}?status=${encodeURIComponent(statusList)}`); };
  const advance = () => { if (idxInQueue >= 0 && idxInQueue + 1 < queue.length) goTo(idxInQueue + 1); else navigate("/invoice-review"); };

  const blocking = form ? evaluateException(form) : "";
  const serverExc = detail?.exception_reason || "";
  const editable = ["pending", "needs_review"].includes(detail?.status);

  const confirmMut = useMutation(
    () => confirmInvoice(invId, {
      vendor: form.vendor, invoice_number: form.invoice_number, invoice_date: form.invoice_date,
      period_start: form.period_start, period_end: form.period_end, currency: form.currency,
      amount_cents: Number(form.amount_cents) || 0, confidence: detail?.confidence,
      notes: detail?.review_note,
    }),
    { onSuccess: () => { toast.success("Invoice imported"); qc.invalidateQueries(["invoices"]); qc.invalidateQueries(["invoice-counts"]); qc.invalidateQueries(["invoice-stats"]); advance(); },
      onError: () => toast.error("Couldn't import. Please try again.") }
  );
  const rejectMut = useMutation(
    () => rejectInvoice(invId, rejNote ? `${rejReason}: ${rejNote}` : rejReason),
    { onSuccess: () => { toast.success("Invoice rejected"); qc.invalidateQueries(["invoices"]); qc.invalidateQueries(["invoice-counts"]); qc.invalidateQueries(["invoice-stats"]); advance(); },
      onError: () => toast.error("Couldn't reject. Please try again.") }
  );

  if (isLoading || !form) return <div className="p-10 text-sm text-text-2">Loading…</div>;

  const fc = detail.field_confidence || {};
  const ring = (key) => (fc[key] != null && fc[key] < UNCERTAIN ? "ring-1 ring-warning" : "");
  const check = (key) => (fc[key] != null && fc[key] >= UNCERTAIN ? <span className="text-success ml-1" aria-label="verified">✓</span> : null);
  const field = (label, key, type = "text") => (
    <div>
      <label htmlFor={`field-${key}`} className="block mb-1 text-sm font-medium text-text">{label}{editable && check(key)}</label>
      {editable ? (
        <input id={`field-${key}`} type={type} value={form[key] || ""} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
          className={`border border-border rounded-ctrl px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-1 focus:ring-primary ${ring(key)}`} />
      ) : (
        <div className="text-sm text-text px-3 py-1.5 border border-transparent">{form[key] || "—"}</div>
      )}
    </div>
  );

  return (
    <Fragment>
      <div className="flex items-center justify-between px-8 py-3 border-b border-card-bd">
        <button type="button" onClick={() => navigate("/invoice-review")} className="text-sm text-primary hover:underline">← Back to Queue</button>
        <div className="text-sm text-text-2">
          {idxInQueue >= 0 ? `Invoice ${idxInQueue + 1} of ${queue.length}` : ""}
          <button type="button" disabled={idxInQueue <= 0} onClick={() => goTo(idxInQueue - 1)} className="ml-3 px-2 disabled:opacity-40">Previous</button>
          <button type="button" disabled={idxInQueue < 0 || idxInQueue + 1 >= queue.length} onClick={() => goTo(idxInQueue + 1)} className="px-2 disabled:opacity-40">Next</button>
        </div>
      </div>
      <div className="text-sm text-text-2 px-8 py-2">
        {detail.vendor || "—"} · {detail.invoice_number || "—"} · {fmtMoney(detail.amount_cents, detail.currency)} · Captured from inbound email
      </div>

      <div className="flex gap-4 px-8 pb-24" style={{ minHeight: "60vh" }}>
        <div className="border border-card-bd rounded-card p-3 bg-surface-2" style={{ flexBasis: "40%" }}>
          {attachments.length > 1 && (
            <div className="flex gap-1 mb-2">
              {attachments.map((a, i) => (
                <button key={a.idx} type="button" onClick={() => setAttIdx(i)}
                  className={`px-2 py-0.5 text-xs rounded ${i === attIdx ? "bg-primary-soft text-primary" : "text-text-2"}`}>{a.filename || `#${a.idx}`}</button>
              ))}
            </div>
          )}
          {previewErr || attachments.length === 0 ? (
            <div className="text-sm text-text-2 p-6 text-center">Original document no longer available (purged after 7 days).</div>
          ) : blobUrl ? (
            // Only inline-preview trusted types: images via <img>, PDFs via a
            // sandboxed <iframe> (no allow-scripts). Any other email-supplied type
            // (e.g. text/html) is offered as a download instead of being rendered
            // same-origin, which would execute embedded scripts.
            (attachments[attIdx]?.content_type || "").startsWith("image/") ? (
              <img src={blobUrl} alt="Invoice document" style={{ maxWidth: "100%" }} />
            ) : (attachments[attIdx]?.content_type || "") === "application/pdf" ? (
              <iframe title="Invoice document" src={blobUrl} sandbox="" style={{ width: "100%", height: "70vh", border: "none" }} />
            ) : (
              <div className="text-sm text-text-2 p-6 text-center">
                Preview isn’t available for this file type.{" "}
                <a href={blobUrl} download={attachments[attIdx]?.filename || "attachment"} className="text-primary hover:underline">Download</a>
              </div>
            )
          ) : <div className="text-sm text-text-2 p-6 text-center">Loading document…</div>}
          <div className="border-t border-card-bd mt-3 pt-2 text-xs text-text-2">
            Received by email · Confidence {detail.confidence != null ? `${Math.round(detail.confidence * 100)}%` : "—"} · {fmtDate(detail.created_at)}
          </div>
        </div>

        <div className="flex-1 flex flex-col gap-4">
          {editable && serverExc && (
            <div className={`rounded-card px-3 py-2 text-sm ${exceptionTone(serverExc) === "critical" ? "bg-danger-soft text-danger" : "bg-warning-soft text-warning"}`}>
              {serverExc}
            </div>
          )}
          {!editable && detail.auto_processed && (
            <div className="rounded-card px-3 py-2 text-sm bg-success-soft text-success">
              Processed automatically{detail.automation_reason ? ` · ${detail.automation_reason}` : ""}
            </div>
          )}
          {!editable && detail.status === "confirmed" && !detail.auto_processed && (
            <div className="rounded-card px-3 py-2 text-sm bg-surface-2 text-text-2">
              Confirmed by {detail.confirmed_by || "—"} · {fmtDate(detail.confirmed_at)}
            </div>
          )}
          {!editable && detail.status === "rejected" && (
            <div className="rounded-card px-3 py-2 text-sm bg-danger-soft text-danger">
              Rejected by {detail.confirmed_by || "—"}{detail.review_note ? ` · ${detail.review_note}` : ""}
            </div>
          )}
          {editable && detail.possible_duplicates?.length > 0 && (
            <div className="rounded-card px-3 py-2 text-sm bg-danger-soft text-danger">
              Possible duplicate of {detail.possible_duplicates.map((d) => d.invoice_number || `#${d.id}`).join(", ")}
            </div>
          )}
          <section><h3 className="text-xs uppercase tracking-wide text-text-2 mb-2">Invoice details</h3>
            <div className="grid grid-cols-2 gap-3">{field("Vendor", "vendor")}{field("Invoice number", "invoice_number")}{field("Invoice date", "invoice_date", "date")}{field("Currency", "currency")}</div>
          </section>
          <section><h3 className="text-xs uppercase tracking-wide text-text-2 mb-2">Billing period</h3>
            <div className="grid grid-cols-2 gap-3">{field("Period start", "period_start", "date")}{field("Period end", "period_end", "date")}</div>
          </section>
          <section><h3 className="text-xs uppercase tracking-wide text-text-2 mb-2">Financial information</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><label htmlFor="field-amount" className="block mb-1 text-sm font-medium text-text">Total amount (EUR){editable && check("amount")}</label>
                {editable ? (
                  <input id="field-amount" type="number" step="0.01" value={eurosFromCents(form.amount_cents)} onChange={(e) => setForm((f) => ({ ...f, amount_cents: centsFromEuros(e.target.value) }))}
                    className={`border border-border rounded-ctrl px-3 py-1.5 text-sm w-full text-right focus:outline-none focus:ring-1 focus:ring-primary ${ring("amount")}`} />
                ) : (
                  <div className="text-sm text-text px-3 py-1.5 text-right border border-transparent">{fmtMoney(detail.amount_cents, detail.currency)}</div>
                )}</div>
            </div>
          </section>
          {detail.timeline?.length > 0 && (
            <section>
              <h3 className="text-xs uppercase tracking-wide text-text-2 mb-2">Activity</h3>
              <ol className="flex flex-col gap-3">
                {detail.timeline.map((ev, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="mt-1 inline-block rounded-full bg-text-3 shrink-0" style={{ width: 8, height: 8 }} />
                    <div className="min-w-0">
                      <div className="text-sm text-text">{ev.label}{ev.actor && ev.actor !== "system" ? ` · ${ev.actor}` : ""}</div>
                      {ev.detail && <div className="text-xs text-text-2">{ev.detail}</div>}
                      <div className="text-xs text-text-3">{fmtDate(ev.at)}</div>
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          )}
        </div>
      </div>

      {editable && (
        <div className="fixed bottom-0 left-0 right-0 border-t border-card-bd bg-card px-8 py-3 flex items-center justify-end gap-2">
          <button type="button" onClick={() => navigate("/invoice-review")} className="px-4 py-1.5 text-sm rounded-ctrl border border-border">Cancel</button>
          <button type="button" onClick={() => setShowReject(true)} className="px-4 py-1.5 text-sm rounded-ctrl border border-danger text-danger">Reject invoice</button>
          <button type="button" onClick={() => confirmMut.mutate()} disabled={!!blocking || confirmMut.isLoading}
            className="px-4 py-1.5 text-sm rounded-ctrl bg-primary text-white disabled:opacity-50"
            title={blocking || ""}>{confirmMut.isLoading ? "Importing…" : "Confirm & import"}</button>
        </div>
      )}

      {editable && showReject && (
        <div className="fixed inset-0 bg-black/30 flex justify-end z-50" onClick={() => setShowReject(false)}>
          <div className="h-full bg-card p-5 flex flex-col gap-3" style={{ width: 384 }} onClick={(e) => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-text">Reject invoice</h3>
            <label className="text-sm text-text-2">Reason
              <select value={rejReason} onChange={(e) => setRejReason(e.target.value)} className="mt-1 border border-border rounded-ctrl px-2 py-1.5 text-sm w-full">
                {REJECT_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            <label className="text-sm text-text-2">Note (optional)
              <input type="text" placeholder="Add a note" value={rejNote} onChange={(e) => setRejNote(e.target.value)} className="mt-1 border border-border rounded-ctrl px-2 py-1.5 text-sm w-full" />
            </label>
            <div className="mt-auto flex gap-2">
              <button type="button" onClick={() => setShowReject(false)} className="px-3 py-1.5 text-sm rounded-ctrl border border-border">Cancel</button>
              <button type="button" onClick={() => rejectMut.mutate()} disabled={rejectMut.isLoading} className="px-3 py-1.5 text-sm rounded-ctrl bg-danger text-white disabled:opacity-50">Reject</button>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};
export default InvoiceReviewDetailPage;
